/* eslint-disable @typescript-eslint/naming-convention */
// deps
import React, { useContext, useCallback, useMemo } from "react";

// internal-deps
import {
    SavingsLineItemData,
    SavingsRightSectionType,
    ReviewedCartApiResponse,
    ApplyCouponsWidgetInfo,
    GiftingApplyCouponWidgetInfo,
} from "@swiggy-private/cart-types";
import {
    getAppliedRestaurantCouponCode,
    getAppliedPaymentCouponCode,
    TooltipContainer,
    getAppliedGiftingCouponCodes,
} from "@swiggy-private/cart-ui";
import { format, isEmpty } from "@swiggy-private/js-utils";

// contexts
import { CartContext } from "@cart-client/contexts/CartContext";

// hooks
import { useCouponSavingsActions } from "./useCouponSavingsActions";
import { useGiftingCouponCodeAnalytics } from "./useGiftingCouponCodeAnalytics";

// helpers
import {
    getAppliedCouponSubtitleText,
    getAppliedCouponTitleText,
    getCouponExpiryInDays,
    getAppliedCouponIcon,
    showCouponCodesListInGiftingCartSavingsCorner,
} from "@cart-client/helpers/Coupon";

// constants
import { APPLY_COUPON_TEXTS, CouponType } from "@cart-client/constants/Coupon";
import { CART_V2_APPLY_COUPON_ICON } from "@cart-client/constants/index";

export interface UseAppliedCouponSavingsReturnType {
    getAppliedCouponSavingsItems: (icon: string, couponType?: CouponType) => SavingsLineItemData[];
}

export const useAppliedCouponSavingsData = (
    applyCouponWidgetData: ReviewedCartApiResponse["apply_coupon_widget_info"],
    applyPaymentCouponWidgetData: ApplyCouponsWidgetInfo["payment_coupon"],
    onSavingsLineItemLoad: (title?: string, subtitle?: string, isPaymentCouponWidget?: boolean) => void,
    isNewPORFlow: boolean,
): UseAppliedCouponSavingsReturnType => {
    const { cartState } = useContext(CartContext);
    const { reviewedCart: cartResponse } = cartState;

    const couponCode = getAppliedRestaurantCouponCode(cartResponse?.data);
    const paymentCouponCode = getAppliedPaymentCouponCode(cartResponse?.data);
    const giftingCouponCodes = getAppliedGiftingCouponCodes(cartResponse?.data);

    const { handleImpressionForGiftingCouponCode } = useGiftingCouponCodeAnalytics();

    const { onApplyCouponSectionClick, onExtraSavingsTooltipClick } = useCouponSavingsActions({
        applyCouponWidgetData,
        applyPaymentCouponWidgetData,
    });

    const extraSavingsData = useMemo(() => {
        if (isEmpty(applyCouponWidgetData) || isEmpty(applyCouponWidgetData.extra_savings)) {
            return null;
        }
        const expiryInDays = getCouponExpiryInDays(applyCouponWidgetData.extra_saving_expiry_in_ms ?? "0");

        return {
            title: format(APPLY_COUPON_TEXTS.X_OFF_ON_NEXT_ORDER, applyCouponWidgetData.extra_savings),
            expiryMessage: format(APPLY_COUPON_TEXTS.EXPIRES_IN_DAYS, String(expiryInDays)),
            tooltipBody: format(APPLY_COUPON_TEXTS.EXTRA_SAVINGS_TOOLTIP_CONTENT, String(expiryInDays)),
        };
    }, [applyCouponWidgetData]);

    const getExtraSavingsLineItem = useCallback(
        (icon: string) => {
            if (isEmpty(extraSavingsData)) {
                return null;
            }

            return {
                icon: icon || CART_V2_APPLY_COUPON_ICON,
                title: {
                    titleNode: (
                        <TooltipContainer
                            displayText={extraSavingsData.title}
                            toolTipMeta={{
                                title: extraSavingsData.title,
                                description: extraSavingsData.tooltipBody,
                            }}
                            onClickTooltipEvent={onExtraSavingsTooltipClick}
                        />
                    ),
                },
                subtitle: {
                    value: extraSavingsData.expiryMessage,
                },
                rightSection: {
                    type: SavingsRightSectionType.LABEL,
                    value: APPLY_COUPON_TEXTS.APPLIED,
                },
                onLoad: () => {
                    onSavingsLineItemLoad(extraSavingsData.title, extraSavingsData.expiryMessage);
                },
            };
        },
        [extraSavingsData, onExtraSavingsTooltipClick, onSavingsLineItemLoad],
    );

    const getAppliedCouponSavingsItems = useCallback(
        (icon: string, couponType: CouponType = CouponType.RESTAURANT): SavingsLineItemData[] => {
            if (couponType === CouponType.RESTAURANT && isEmpty(couponCode) && isEmpty(giftingCouponCodes)) {
                return [];
            }

            if (couponType === CouponType.PAYMENT && isEmpty(paymentCouponCode)) {
                return [];
            }

            const titleText = getAppliedCouponTitleText({
                couponType,
                applyCouponWidgetData,
                applyPaymentCouponWidgetData,
                couponCode,
                paymentCouponCode,
                giftingCouponCodes,
            });
            const subtitleText = getAppliedCouponSubtitleText(couponType, isNewPORFlow);

            let couponCodeList;
            if (showCouponCodesListInGiftingCartSavingsCorner(applyCouponWidgetData)) {
                couponCodeList = {
                    applied: true,
                    codes: (applyCouponWidgetData as GiftingApplyCouponWidgetInfo)?.coupon_codes,
                    impressionEventCallback: handleImpressionForGiftingCouponCode,
                };
            }

            const appliedSavingsLineItem: SavingsLineItemData = {
                icon: icon || getAppliedCouponIcon(couponType),
                title: {
                    value: titleText,
                },
                subtitle: {
                    value: subtitleText,
                    icon: true,
                    onClick: () =>
                        onApplyCouponSectionClick({ isPaymentCouponWidget: couponType === CouponType.PAYMENT }),
                },
                rightSection: {
                    type: SavingsRightSectionType.LABEL,
                    value: APPLY_COUPON_TEXTS.APPLIED,
                },
                onLoad: () => {
                    onSavingsLineItemLoad(titleText, subtitleText, couponType === CouponType.PAYMENT);
                },
                couponCodeList,
            };

            const appliedExtraSavingsLineItem = getExtraSavingsLineItem(icon);

            return appliedExtraSavingsLineItem && couponType === CouponType.RESTAURANT
                ? [appliedSavingsLineItem, appliedExtraSavingsLineItem]
                : [appliedSavingsLineItem];
        },
        [
            applyCouponWidgetData,
            applyPaymentCouponWidgetData,
            couponCode,
            getExtraSavingsLineItem,
            giftingCouponCodes,
            isNewPORFlow,
            onApplyCouponSectionClick,
            onSavingsLineItemLoad,
            paymentCouponCode,
            handleImpressionForGiftingCouponCode,
        ],
    );

    return { getAppliedCouponSavingsItems };
};
